import type React from 'react';
import { useContext, useEffect } from 'react';
import type { FormApi } from 'final-form';
import { Form as ReactFinalForm } from 'react-final-form';
import {
  AddressField,
  CityField,
  DateOfBirthField,
  FirstNameField,
  LastNameField,
  MiddleNameField,
  PhoneNumberField,
  SocialSecurityNumberField,
  StateField,
  ZipCodeField,
} from './Fields';
import { Box, Form, Heading, Paragraph, Popover, TextButton } from '@zillow/constellation-10';
import { TermSet } from '../../../../../models';
import { useMount } from 'ahooks';
import { LANDLORD_CLICKSTREAM_EVENTS } from '../../../clickstream/events';
import { WizardContext } from '../../../wizard/WizardContext';
import { useIdVerificationCallbacks, usePidAnswers, useSetPidAnswers, useTermSet } from '../../../IdvContext';

/* eslint-disable curly-quotes/no-straight-quotes */
const renterTemplateAreaXl = `
  "head head head head head head"
  "subHead subHead subHead subHead subHead subHead"
  "first first first first first first"
  "middle middle middle middle middle middle"
  "last last last last last last"
  "address address address address address address"
  "city city state state zip zip"
  "dob dob dob phone phone phone"
  "ssn ssn ssn . . ."
  "popper popper popper popper popper popper"
`;

const renterTemplateAreaLg = `
  "head head head head head head"
  "subHead subHead subHead subHead subHead subHead"
  "first first first first first first"
  "middle middle middle middle middle middle"
  "last last last last last last"
  "address address address address address address"
  "city   city  city  city  city  city"
  "state  state state state state state"
  "zip    zip   zip   zip   zip   zip"
  "dob dob dob phone phone phone"
  "ssn ssn ssn . . ."
  "popper popper popper popper popper popper"
`;

const landlordTemplateArea = `
  "head head head head head head"
  "subHead subHead subHead subHead subHead subHead"
  "first first first first first first"
  "middle middle middle middle middle middle"
  "last last last last last last"
  "address address address address address address"
  "city city state state zip zip"
  "ssn ssn ssn . . ."
  "popper popper popper popper popper popper"
`;

const defaultRenterTemplateArea = `
  "head"
  "subHead"
  "first"
  "middle"
  "last"
  "address"
  "city"
  "state"
  "zip"
  "dob"
  "phone"
  "ssn"
  "popper"
`;

const defaultLandlordTemplateArea = `
  "head"
  "subHead"
  "first"
  "middle"
  "last"
  "address"
  "city"
  "state"
  "zip"
  "ssn"
  "popper"
`;
/* eslint-enable curly-quotes/no-straight-quotes */

function PersonalInfoFormBody({ form, invalid }: { form: FormApi; invalid: boolean }) {
  const { setOnNext, setIsNextDisabled } = useContext(WizardContext);
  const termSet = useTermSet();

  useEffect(() => {
    setOnNext(() => () => {
      form.submit();
    });
    return () => setOnNext(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form's object identity is unstable across renders; form.submit is the stable reference we actually depend on
  }, [form.submit, setOnNext]);

  useEffect(() => {
    setIsNextDisabled(invalid);
    return () => setIsNextDisabled(false);
  }, [invalid, setIsNextDisabled]);

  return (
    <Form>
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: {
            base: 'repeat(1, 1fr)',
            lg: termSet === TermSet.RenterScreening ? 'repeat(6, 1fr)' : 'repeat(1, 1fr)',
            xl: 'repeat(6, 1fr)',
          },
          gap: 'loose',
          gridTemplateAreas: {
            base: termSet === TermSet.LandlordScreening ? defaultLandlordTemplateArea : defaultRenterTemplateArea,
            lg: termSet === TermSet.LandlordScreening ? defaultLandlordTemplateArea : renterTemplateAreaLg,
            xl: termSet === TermSet.LandlordScreening ? landlordTemplateArea : renterTemplateAreaXl,
          },
        }}
      >
        <Box css={{ gridArea: 'head' }}>
          <Heading level={2} textStyle="heading-sm">
            Provide your details for verification
          </Heading>
        </Box>
        <Box css={{ gridArea: 'subHead' }}>
          <Paragraph>
            {termSet === TermSet.RenterScreening
              ? 'This information will be sent to Experian and CIC to verify your identity and create credit and background reports.'
              : 'This information helps our partners, Experian and CIC, verify your identity so you can review applicants’ information and find a suitable renter.'}
          </Paragraph>
        </Box>
        <Box css={{ gridArea: 'first' }}>
          <FirstNameField />
        </Box>
        <Box css={{ gridArea: 'middle' }}>
          <MiddleNameField />
        </Box>
        <Box css={{ gridArea: 'last' }}>
          <LastNameField />
        </Box>
        <Box css={{ gridArea: 'address' }}>
          <AddressField />
        </Box>
        <Box css={{ gridArea: 'city' }}>
          <CityField />
        </Box>
        <Box css={{ gridArea: 'state' }}>
          <StateField />
        </Box>
        <Box css={{ gridArea: 'zip' }}>
          <ZipCodeField />
        </Box>
        {termSet === TermSet.RenterScreening && (
          <>
            <Box css={{ gridArea: 'dob' }}>
              <DateOfBirthField />
            </Box>
            <Box css={{ gridArea: 'phone' }}>
              <PhoneNumberField />
            </Box>
          </>
        )}
        <Box css={{ gridArea: 'ssn' }}>
          <SocialSecurityNumberField />
        </Box>
        <Box css={{ gridArea: 'popper' }}>
          <Popover
            trigger={
              <TextButton onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => e.preventDefault()}>
                Why we ask for your Social Security number
              </TextButton>
            }
            header={
              <Heading level={3} textStyle="heading-sm">
                Social Security number (SSN)
              </Heading>
            }
            body={
              <Paragraph>
                Providing an SSN helps verify your identity faster, but isn’t required. If this field is left blank,
                Experian may need to ask additional questions to verify your identity.
              </Paragraph>
            }
          />
        </Box>
      </Box>
    </Form>
  );
}

export default function PersonalInfoStep() {
  const { goToNextStep } = useContext(WizardContext);
  const pidAnswers = usePidAnswers();
  const setPidAnswers = useSetPidAnswers();
  const { trackEvent } = useIdVerificationCallbacks();

  useMount(() => {
    trackEvent(LANDLORD_CLICKSTREAM_EVENTS.viewPage('id_verification_provide_details'));
  });

  return (
    <ReactFinalForm
      initialValues={pidAnswers}
      onSubmit={async (formValues) => {
        setPidAnswers((currentPidAnswers) => ({
          ...currentPidAnswers,
          ...formValues,
          /*
            middleName and ssn are optional fields so they must be set to
            an empty string if their values are cleared out otherwise react-final-form
            will default them to ‘undefined’ resulting in their value never
            being updated. See the link below form more info:
            https://final-form.org/docs/final-form/faq#why-does-final-form-set-my--field-value-to-undefined
          */
          middleName: formValues?.middleName ?? '',
          ssn: formValues?.ssn ?? '',
        }));
        goToNextStep();
      }}
      render={({ form, invalid }) => <PersonalInfoFormBody form={form} invalid={invalid} />}
    />
  );
}
